import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, User, Ticket, Loader2, CheckCircle2, Bell, Flame } from "lucide-react";
import { InviteFriendButton } from "@/components/InviteFriendButton";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

const req = async (url, method = "GET", body) => {
  const res = await fetch(url, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || "İşlem başarısız");
  return d;
};

const dayLabel = (d) => format(new Date(`${d}T12:00:00`), "d MMMM EEEE", { locale: tr });

const ClassItem = ({ c, biz, onDone }) => {
  const [picking, setPicking] = useState(false);
  const pkgs = biz.packages || [];
  const [payment, setPayment] = useState("");
  const [mpId, setMpId] = useState("");
  const [busy, setBusy] = useState(false);
  const full = c.available <= 0;
  const options = [
    ...biz.cards.map((x) => ({ v: `card:${x}`, l: x })),
    ...(pkgs.length ? [{ v: "package", l: "Paketim var" }] : []),
    { v: "package_request", l: "Paket almak istiyorum", wide: !pkgs.length },
  ];
  const pick = (v) => { setPayment(v); setMpId(v === "package" && pkgs.length === 1 ? pkgs[0].mp_id : ""); };
  const ready = !!payment && (payment !== "package" || !!mpId);

  const join = async () => {
    if (!payment) return toast.error("Lütfen kart veya paket seçin");
    if (payment === "package" && !mpId) return toast.error("Lütfen kullanmak istediğiniz paketi seçin");
    setBusy(true);
    try {
      const r = await req(`${API}/member/open-classes/${c.class_id}/join`, "POST", { payment, ...(payment === "package" ? { member_package_id: mpId } : {}) });
      toast.success(r.package_found === false ? "Yeriniz ayrıldı. Paketinizi derse geldiğinizde stüdyodan alabilirsiniz." : "Derse kaydoldunuz");
      setPicking(false);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Bu ders katılımınızı iptal etmek istiyor musunuz?")) return;
    setBusy(true);
    try { await req(`${API}/reservations/${c.my_reservation_id}/cancel`, "PUT", { reason: "Üye panelinden iptal" }); toast.success("İptal edildi"); onDone(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const joinWaitlist = async () => {
    setBusy(true);
    try { const r = await req(`${API}/member/open-classes/${c.class_id}/waitlist`, "POST", {}); toast.success(`Bekleme listesine eklendiniz. Sıranız: ${r.position}`); onDone(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const leaveWaitlist = async () => {
    setBusy(true);
    try { await req(`${API}/member/open-classes/waitlist/${c.my_waitlist.waitlist_id}`, "DELETE"); toast.success("Bekleme listesinden çıkarıldınız"); onDone(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`member-open-class-${c.class_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-lg font-bold text-navy flex items-center gap-1.5"><Clock className="w-4 h-4 text-emerald-600" />{c.start_time}–{c.end_time}</div>
        <span className="text-sm text-grey">{[c.branch, c.class_type].filter(Boolean).join(" · ") || "Ders"}</span>
        <span className="text-sm text-grey flex items-center gap-1"><User className="w-3.5 h-3.5" />{c.trainer_name}</span>
        <div className="flex-1" />
        {c.my_reservation_id ? (
          <Badge className="bg-emerald-100 text-emerald-800" data-testid={`member-open-class-joined-${c.class_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Kayıtlısınız</Badge>
        ) : (
          <Badge className={full ? "bg-slate-200 text-slate-600" : "bg-emerald-50 text-emerald-700"} data-testid={`member-open-class-status-${c.class_id}`}>{full ? "Dolu" : `${c.available} yer kaldı`}</Badge>
        )}
      </div>
      {!c.my_reservation_id && c.available === 1 && (
        <p className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1 animate-pulse" data-testid={`member-open-class-last-spot-${c.class_id}`}>
          <Flame className="w-3.5 h-3.5" /> Son 1 yer!
        </p>
      )}
      {c.participants?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`member-open-class-participants-${c.class_id}`}>
          <span className="text-xs text-grey">Katılımcılar:</span>
          {c.participants.map((p, i) => <span key={i} className="text-xs rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{p}</span>)}
        </div>
      )}
      {c.my_reservation_id ? (
        <div className="flex flex-wrap gap-2">
          {biz.booking_token && (
            <InviteFriendButton testId={`member-open-class-invite-${c.class_id}`} className="h-9 px-3 text-sm"
              businessName={biz.business_name} lesson={[c.branch, c.class_type].filter(Boolean).join(" ")} date={c.date} startTime={c.start_time} endTime={c.end_time}
              link={`${window.location.origin}/rezervasyon/${biz.booking_token}`} />
          )}
          <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200" disabled={busy} onClick={cancel} data-testid={`member-open-class-cancel-${c.class_id}`}>Katılımı İptal Et</Button>
        </div>
      ) : !full && !picking ? (
        <Button size="sm" className="rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={() => setPicking(true)} data-testid={`member-open-class-join-${c.class_id}`}>Katıl</Button>
      ) : full && c.my_waitlist ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`member-waitlist-state-${c.class_id}`}>
          <Badge className="bg-amber-100 text-amber-800"><Bell className="w-3.5 h-3.5 mr-1" />Bekleme listesindesiniz · Sıranız: {c.my_waitlist.position}</Badge>
          <Button size="sm" variant="outline" className="rounded-xl" disabled={busy} onClick={leaveWaitlist} data-testid={`member-waitlist-leave-${c.class_id}`}>Listeden çık</Button>
        </div>
      ) : full ? (
        <Button size="sm" variant="outline" className="rounded-xl border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" disabled={busy} onClick={joinWaitlist} data-testid={`member-waitlist-join-${c.class_id}`}>
          <Bell className="w-4 h-4 mr-1" />Yer açılırsa haber ver
        </Button>
      ) : null}
      {picking && (
        <div className="space-y-2" data-testid={`member-open-class-picker-${c.class_id}`}>
          <p className="text-sm font-medium text-navy">Nasıl katılacaksınız?</p>
          <div className="grid grid-cols-2 gap-2">
            {options.map((o) => (
              <button key={o.v} type="button" onClick={() => pick(o.v)}
                className={`${o.wide ? "col-span-2 " : ""}rounded-xl border px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${payment === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:border-slate-400"}`}
                data-testid={`member-pay-${o.v.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-${c.class_id}`}>
                <Ticket className="w-4 h-4" />{o.l}
              </button>
            ))}
          </div>
          {payment === "package" && (
            <div className="space-y-1.5" data-testid={`member-package-list-${c.class_id}`}>
              <p className="text-xs text-grey">Hangi paketinizi kullanmak istersiniz?</p>
              {pkgs.map((p) => (
                <button key={p.mp_id} type="button" onClick={() => setMpId(p.mp_id)}
                  className={`w-full rounded-xl border px-3 py-2 text-left flex items-center justify-between gap-2 transition-colors ${mpId === p.mp_id ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`member-package-${p.mp_id}-${c.class_id}`}>
                  <span className="text-sm font-medium text-navy">{p.name}{p.end_date && <span className="block text-[11px] font-normal text-grey">Son kullanım: {p.end_date.split("-").reverse().join(".")}</span>}</span>
                  <span className="shrink-0 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800 px-2 py-1">{p.remaining_sessions} seans kaldı</span>
                </button>
              ))}
            </div>
          )}
          {!payment && <p className="text-[11px] text-grey">Devam etmek için bir seçim yapın.</p>}
          <div className="flex gap-2">
            <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700" disabled={busy || !ready} onClick={join} data-testid={`member-open-class-confirm-${c.class_id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Katılımı Onayla"}</Button>
            <Button variant="ghost" className="rounded-xl" onClick={() => setPicking(false)}>Vazgeç</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function MemberGroupClasses() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await req(`${API}/member/open-classes`)); } catch (e) { toast.error(e.message); setData([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-grey" /></div>;
  return (
    <div className="space-y-6" data-testid="member-group-classes">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-navy flex items-center gap-2"><Users className="w-6 h-6 text-emerald-600" />Online Ders Programı</h1>
        <p className="text-sm text-grey mt-1">Önümüzdeki 7 günün açık dersleri (grup, düet ve özel ders). Yer varsa hemen katılabilirsiniz.</p>
      </div>
      {!data.length && <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-grey" data-testid="member-group-classes-empty">Üye olduğunuz işletmelerde şu an açık ders yok.</CardContent></Card>}
      {data.map((biz) => {
        const byDay = biz.classes.reduce((a, c) => ({ ...a, [c.date]: [...(a[c.date] || []), c] }), {});
        return (
          <Card key={biz.business_id} className="border-0 shadow-sm" data-testid={`member-group-biz-${biz.business_id}`}>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <h2 className="text-lg font-semibold text-navy">{biz.business_name}</h2>
                <span className="text-xs text-grey">İptal: dersten en geç {biz.cancel_hours} saat önce</span>
              </div>
              {!biz.classes.length && <p className="text-sm text-grey">Önümüzdeki 7 günde açık ders yok.</p>}
              {Object.entries(byDay).map(([d, list]) => (
                <div key={d} className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600 capitalize">{dayLabel(d)}</p>
                  {list.map((c) => <ClassItem key={c.class_id} c={c} biz={biz} onDone={load} />)}
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
